import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { fileUrl, api } from "@/lib/api";
import { GALLERY_FILTERS } from "@/data/site";
import PageHeader from "@/components/PageHeader";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/hooks/useSeo";

export default function Gallery() {
  useSeo("Gallery | Our Work | StoneCrest Exterior Specialists", "See the quality of StoneCrest's exterior cleaning work across Lancashire — roof cleaning, render softwashing, driveways, gutters and fascia & soffit cleaning.", "https://stonecrestexteriors.co.uk/gallery");
  const [filter, setFilter] = useState("All");
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/gallery").then((r) => setItems(r.data)).catch(() => {}); }, []);
  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div>
      <PageHeader overline="Our Work" lines={["The StoneCrest", "standard."]} subtitle="A look at the properties we've transformed. Every finish reflects our commitment to premium, careful exterior care." />

      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-24 md:pb-32">
        <div className="flex flex-wrap gap-2 md:gap-3 mb-12" data-testid="gallery-filters">
          {GALLERY_FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`filter-${f.replace(/[^a-z]/gi, "-").toLowerCase()}`}
              className={`px-5 py-2.5 text-sm tracking-wide border transition-colors ${filter === f ? "bg-[var(--oxblood)] text-white border-[var(--oxblood)]" : "border-[var(--border)] text-[var(--ink)] hover:border-[var(--oxblood)]"}`}>
              {f}
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <p className="text-[var(--muted)] font-serif text-2xl italic py-16">Images coming soon.</p>
        ) : (
          <motion.div layout className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            <AnimatePresence mode="popLayout">
              {filtered.map((item, i) => (
                <motion.div
                  key={item.id} layout
                  initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                  className={`group relative overflow-hidden ${i % 5 === 0 ? "lg:col-span-2 lg:row-span-2" : ""}`}
                  data-testid={`gallery-item-${item.id}`}
                >
                  <div className={`${i % 5 === 0 ? "aspect-[4/3] lg:h-full" : "aspect-[4/5]"} overflow-hidden`}>
                    <img src={fileUrl(item.image_url)} alt={item.caption || item.category} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex items-end p-5">
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.2em] text-white/70">{item.category}</span>
                      {item.caption && <p className="font-serif text-white text-xl">{item.caption}</p>}
                    </div>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
        )}
      </section>
      <CTASection />
    </div>
  );
}
